// src/pages/SdtcPage.js — GFM3 RH
// ============================================================================
//  §3.58 — SOLDE DE TOUT COMPTE (droits de fin de contrat)
//
//  Reproduit le classeur « Template calcul SDTC CDI CDD GFM3 PROJET.xlsx ».
//  Le calcul affiché ici est purement indicatif : la valeur enregistrée est
//  systématiquement recalculée par le serveur (SdtcController::calculer).
// ============================================================================

import React, { useEffect, useState, useCallback, useRef } from 'react';
import {
  getSdtcList, getSdtc, preparerSdtc,
  createSdtc, updateSdtc, deleteSdtc, getEmployes,
} from '../services/api';
import { useAuth } from '../hooks/useAuth';
import { useExportExcel, fmtDate } from '../hooks/useExportExcel';

/* ─── Constantes ────────────────────────────────────────────────────────── */
const COEF_CALENDAIRE   = 1.25;
const SEUIL_EXONERATION = 50000;

const fmt = (v) => (v == null || v === '' || isNaN(v))
  ? '0' : Number(v).toLocaleString('fr-FR', { maximumFractionDigits: 0 });

const pct = (v) => `${(Number(v) * 100).toFixed(0)} %`;

const FORM_VIDE = {
  employe_id: '', nom_prenoms: '', matricule: '', type_contrat: 'CDI',
  salaire_base: 0, salaire_presence: 0, salaire_brut_contractuel: 0,
  date_embauche: '', date_depart: '',
  anciennete_ans: 0, anciennete_mois: 0, anciennete_jours: 0, anciennete_heures: 0,
  conges_non_pris: 0,
  preavis_sursalaire: 0, preavis_prime_anciennete: 0,
  preavis_forfait_hs: 0, preavis_avantages: 0, preavis_nb_mois: 0,
  gratification_taux: '', gratification_prorata: 0, rappel_anciennete: 0,
  transport_imp_montant: 0, transport_imp_prorata: 0,
  transport_nonimp_montant: 0, transport_nonimp_prorata: 0,
  ded_licenciement: false, ded_salaire_presence: false, ded_conges: false,
  ded_gratification: false, ded_transport: false, ded_preavis: false,
  ded_rappel_anciennete: false,
  statut: 'Brouillon', commentaire: '',
  salaires: [],
};

/* ─── Calcul local (aperçu instantané) ──────────────────────────────────── */
function calculerLocal(f) {
  const type = f.type_contrat === 'CDD' ? 'CDD' : 'CDI';
  const nums = (f.salaires || []).map(s => parseFloat(s.montant) || 0).filter(m => m > 0);
  const sbm   = nums.length ? nums.reduce((a, b) => a + b, 0) / nums.length : 0;
  const cumul = nums.reduce((a, b) => a + b, 0);

  const ans    = parseFloat(f.anciennete_ans) || 0;
  const mois   = parseFloat(f.anciennete_mois) || 0;
  const jours  = parseFloat(f.anciennete_jours) || 0;
  const heures = parseFloat(f.anciennete_heures) || 0;
  const ancAnnees = ans + mois / 12 + jours / 360 + heures / 2880;

  let taux;
  if (type === 'CDD')      taux = 0.03;
  else if (ancAnnees <= 5)  taux = 0.30;
  else if (ancAnnees <= 10) taux = 0.35;
  else                      taux = 0.40;

  let tranches, totalLic;
  if (type === 'CDI') {
    tranches = [
      { libelle: '1re tranche — années', quotite: ans,            montant: sbm * taux * ans },
      { libelle: '2e tranche — mois',    quotite: mois / 12,      montant: sbm * taux * (mois / 12) },
      { libelle: '3e tranche — jours',   quotite: jours / 360,    montant: sbm * taux * (jours / 360) },
      { libelle: '4e tranche — heures',  quotite: heures / 2880,  montant: sbm * taux * (heures / 2880) },
    ];
    totalLic = tranches.reduce((s, t) => s + t.montant, 0);
  } else {
    tranches = [{ libelle: 'Indemnité de fin de contrat', quotite: cumul, montant: taux * cumul }];
    totalLic = taux * cumul;
  }

  // §3.59 — le salaire de base est saisi une seule fois, en haut du formulaire
  const basePreavis = (parseFloat(f.salaire_base) || 0)
    + ['preavis_sursalaire', 'preavis_prime_anciennete', 'preavis_forfait_hs', 'preavis_avantages']
      .reduce((s, k) => s + (parseFloat(f[k]) || 0), 0);
  const totalPreavis = basePreavis * (parseFloat(f.preavis_nb_mois) || 0);

  const contractuel = parseFloat(f.salaire_brut_contractuel) || 0;
  const refConges = (type === 'CDD' && contractuel > 0) ? contractuel : sbm;
  const congesCal = (parseFloat(f.conges_non_pris) || 0) * COEF_CALENDAIRE;
  const indConges = (refConges / 30) * congesCal;

  const gTaux = f.gratification_taux === '' || f.gratification_taux == null
    ? null : parseFloat(f.gratification_taux);
  const baseGratif = contractuel > 0 ? contractuel : sbm;
  const gratification = gTaux == null
    ? 0 : baseGratif * gTaux * (parseFloat(f.gratification_prorata) || 0);

  const trImp    = (parseFloat(f.transport_imp_montant) || 0) * (parseFloat(f.transport_imp_prorata) || 0);
  const trNonImp = (parseFloat(f.transport_nonimp_montant) || 0) * (parseFloat(f.transport_nonimp_prorata) || 0);
  const trTotal  = trImp + trNonImp;

  const lignes = [
    { code: 'licenciement', libelle: type === 'CDD'
        ? 'Indemnité de fin de contrat' : 'Indemnité de licenciement ou de départ',
      deduction: !!f.ded_licenciement, montant: f.ded_licenciement ? 0 : totalLic },
    { code: 'salaire_presence', libelle: 'Salaire de présence',
      deduction: !!f.ded_salaire_presence,
      montant: f.ded_salaire_presence ? 0 : (parseFloat(f.salaire_presence) || 0) },
    { code: 'conges', libelle: 'Indemnité de congé payé',
      deduction: !!f.ded_conges, montant: f.ded_conges ? 0 : indConges },
    { code: 'gratification', libelle: 'Gratification annuelle',
      deduction: !!f.ded_gratification, montant: f.ded_gratification ? 0 : gratification },
    { code: 'transport', libelle: 'Indemnité de transport',
      deduction: !!f.ded_transport, montant: f.ded_transport ? 0 : trTotal },
    { code: 'preavis', libelle: 'Indemnité compensatrice de préavis',
      deduction: !!f.ded_preavis, montant: f.ded_preavis ? -totalPreavis : 0 },
  ];
  if (type === 'CDI') {
    lignes.push({ code: 'rappel_anciennete', libelle: "Rappel prime d'ancienneté",
      deduction: !!f.ded_rappel_anciennete,
      montant: f.ded_rappel_anciennete ? 0 : (parseFloat(f.rappel_anciennete) || 0) });
  }

  const sdtcTotal = lignes.reduce((s, l) => s + l.montant, 0);
  const licRetenu = f.ded_licenciement ? 0 : totalLic;
  const licNonImp = licRetenu >= SEUIL_EXONERATION ? licRetenu * 0.5 : licRetenu;
  const trNonImpRetenu = f.ded_transport ? 0 : trNonImp;
  const quotePart = sdtcTotal - licNonImp - trNonImpRetenu;

  return {
    type_contrat: type, nb_salaires: nums.length,
    salaire_brut_moyen: sbm, cumul_salaires: cumul,
    anciennete_annees: ancAnnees, taux_indemnite: taux,
    tranches, total_licenciement: totalLic,
    base_preavis: basePreavis, total_preavis: totalPreavis,
    conges_calendaires: congesCal, indemnite_conges: indConges,
    gratification, transport_imposable: trImp, transport_non_imposable: trNonImp,
    transport_total: trTotal, lignes_sdtc: lignes, sdtc_total: sdtcTotal,
    licenciement_non_imp: licNonImp, quote_part_imposable: quotePart,
  };
}

/* ─── Styles ────────────────────────────────────────────────────────────── */
const S = {
  root:   { fontFamily: "'DM Sans','Segoe UI',sans-serif", color: '#1e293b' },
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '20px 28px 0', gap: 12 },
  title:  { fontSize: 22, fontWeight: 800, color: '#0f2644', margin: 0 },
  sub:    { fontSize: 12.5, color: '#64748b', margin: '4px 0 0' },
  tabBar: { display: 'flex', gap: 4, padding: '16px 28px 0', borderBottom: '1px solid #e2e8f0' },
  tab: (a) => ({
    padding: '10px 18px', fontSize: 13.5, fontWeight: a ? 700 : 500, cursor: 'pointer',
    background: 'none', border: 'none', fontFamily: 'inherit',
    color: a ? '#0f2644' : '#64748b',
    borderBottom: a ? '3px solid #0f2644' : '3px solid transparent',
  }),
  card:  { background: '#fff', border: '1px solid #e2e8f0', borderRadius: 10, margin: '16px 28px' },
  head:  { display: 'flex', justifyContent: 'space-between', alignItems: 'center',
           padding: '14px 18px', borderBottom: '1px solid #e2e8f0', flexWrap: 'wrap', gap: 10 },
  cardTitle: { fontWeight: 700, fontSize: 14, color: '#0f2644' },
  body:  { padding: 18 },
  grid:  { display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(210px,1fr))', gap: 14 },
  label: { display: 'block', fontSize: 11.5, fontWeight: 600, color: '#475569', marginBottom: 4 },
  input: { width: '100%', padding: '8px 10px', border: '1px solid #cbd5e1', borderRadius: 7,
           fontSize: 13, fontFamily: 'inherit', boxSizing: 'border-box' },
  section: { fontWeight: 800, fontSize: 13, color: '#0f2644', margin: '20px 0 10px',
             paddingTop: 12, borderTop: '1px solid #e8edf3' },
  th: { background: '#0f2644', color: '#fff', padding: '8px 10px', fontSize: 11.5,
        textAlign: 'left', fontWeight: 700 },
  td: { borderBottom: '1px solid #e2e8f0', padding: '7px 10px', fontSize: 12.5 },
  btn: { padding: '9px 16px', borderRadius: 8, border: 'none', fontSize: 13, fontWeight: 700,
         cursor: 'pointer', fontFamily: 'inherit' },
};
const btnP = { ...S.btn, background: '#0f2644', color: '#fff' };
const btnG = { ...S.btn, background: '#f1f5f9', color: '#334155' };
const btnV = { ...S.btn, background: '#15803d', color: '#fff' };

/* ─── Composant principal ───────────────────────────────────────────────── */
export default function SdtcPage() {
  const { user } = useAuth();
  const isAdmin = user?.role !== 'employe' && user?.role !== 'lecteur';
  const { exporting, exportToExcel } = useExportExcel();

  const [tab, setTab]         = useState('liste');
  const [liste, setListe]     = useState([]);
  const [loading, setLoading] = useState(true);
  const [employes, setEmployes] = useState([]);
  const [form, setForm]       = useState(FORM_VIDE);
  const [editId, setEditId]   = useState(null);
  const [saving, setSaving]   = useState(false);
  const [toast, setToast]     = useState(null);
  const [rech, setRech]       = useState('');
  const [pdfGen, setPdfGen]   = useState(false);
  const ficheRef              = useRef(null);

  const res = calculerLocal(form);

  const showToast = (msg, type = 'success') => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 4000);
  };

  const charger = useCallback(async () => {
    setLoading(true);
    try {
      const r = await getSdtcList();
      setListe(Array.isArray(r.data) ? r.data : []);
    } catch { setListe([]); }
    setLoading(false);
  }, []);

  useEffect(() => { charger(); }, [charger]);

  useEffect(() => {
    getEmployes({ limit: 1000 })
      .then(r => {
        const raw = r.data;
        setEmployes(Array.isArray(raw) ? raw : (raw?.data || []));
      })
      .catch(() => setEmployes([]));
  }, []);

  const f = (k) => (e) => {
    const v = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    setForm(p => ({ ...p, [k]: v }));
  };

  /* Pré-remplissage depuis la fiche employé */
  const choisirEmploye = async (empId) => {
    if (!empId) { setForm(p => ({ ...p, employe_id: '' })); return; }
    try {
      const r = await preparerSdtc(empId);
      const d = r.data?.data || {};
      setForm(p => ({
        ...p, ...d,
        employe_id: empId,
        salaires: (d.salaires || []).length
          ? d.salaires
          : Array.from({ length: d.nb_mois_reference || 12 },
              (_, i) => ({ rang: i + 1, periode: '', montant: 0 })),
      }));
      showToast('Données reprises depuis la fiche employé');
    } catch {
      showToast('Impossible de pré-remplir depuis la fiche', 'error');
    }
  };

  const majSalaire = (idx, champ, valeur) => {
    setForm(p => ({
      ...p,
      salaires: p.salaires.map((s, i) => i === idx ? { ...s, [champ]: valeur } : s),
    }));
  };

  const nbMoisRef = form.type_contrat === 'CDD' ? 24 : 12;

  const ajusterNbSalaires = (type) => {
    const n = type === 'CDD' ? 24 : 12;
    setForm(p => {
      const cur = p.salaires || [];
      const suite = cur.length >= n
        ? cur.slice(0, n)
        : [...cur, ...Array.from({ length: n - cur.length },
            (_, i) => ({ rang: cur.length + i + 1, periode: '', montant: 0 }))];
      return { ...p, type_contrat: type, salaires: suite.map((s, i) => ({ ...s, rang: i + 1 })) };
    });
  };

  const nouveau = () => { setForm(FORM_VIDE); setEditId(null); setTab('form'); };

  const ouvrir = async (id) => {
    try {
      const r = await getSdtc(id);
      const d = r.data;
      setForm({
        ...FORM_VIDE, ...d,
        gratification_taux: d.gratification_taux == null ? '' : d.gratification_taux,
        ded_licenciement:      !!Number(d.ded_licenciement),
        ded_salaire_presence:  !!Number(d.ded_salaire_presence),
        ded_conges:            !!Number(d.ded_conges),
        ded_gratification:     !!Number(d.ded_gratification),
        ded_transport:         !!Number(d.ded_transport),
        ded_preavis:           !!Number(d.ded_preavis),
        ded_rappel_anciennete: !!Number(d.ded_rappel_anciennete),
        salaires: d.salaires || [],
      });
      setEditId(id);
      setTab('form');
    } catch { showToast('Erreur de chargement', 'error'); }
  };

  const enregistrer = async (statut) => {
    if (!form.employe_id) { showToast('Sélectionnez un employé', 'error'); return; }
    setSaving(true);
    try {
      const payload = { ...form, statut: statut || form.statut };
      if (editId) await updateSdtc(editId, payload);
      else        await createSdtc(payload);
      showToast(editId ? 'Solde de tout compte mis à jour' : 'Solde de tout compte enregistré');
      setTab('liste');
      setEditId(null);
      charger();
    } catch (e) {
      showToast(e?.response?.data?.message || 'Erreur lors de l\'enregistrement', 'error');
    }
    setSaving(false);
  };

  const supprimer = async (id) => {
    if (!window.confirm('Supprimer définitivement ce solde de tout compte ?')) return;
    try { await deleteSdtc(id); showToast('Supprimé'); charger(); }
    catch { showToast('Erreur de suppression', 'error'); }
  };

  const genererPDF = async () => {
    if (!ficheRef.current) return;
    setPdfGen(true);
    try {
      const [{ default: jsPDF }, { default: html2canvas }] = await Promise.all([
        import('jspdf'), import('html2canvas'),
      ]);
      const canvas = await html2canvas(ficheRef.current, {
        scale: 2, useCORS: true, backgroundColor: '#ffffff', logging: false,
      });
      const pdf = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
      const pw = pdf.internal.pageSize.getWidth();
      const ph = pdf.internal.pageSize.getHeight();
      const img = canvas.toDataURL('image/png');
      const marge = 6;
      const ratio = Math.min((pw - marge * 2) / canvas.width, (ph - marge * 2) / canvas.height);
      const w = canvas.width * ratio, h = canvas.height * ratio;
      pdf.addImage(img, 'PNG', (pw - w) / 2, marge, w, h);
      pdf.save(`SDTC_${(form.nom_prenoms || '').replace(/\s+/g, '_')}_${form.type_contrat}.pdf`);
    } catch {
      showToast('Erreur PDF : vérifiez jspdf et html2canvas', 'error');
    }
    setPdfGen(false);
  };

  const listeFiltree = liste.filter(s => {
    const q = rech.trim().toLowerCase();
    if (!q) return true;
    return [s.nom, s.prenoms, s.matricule, s.type_contrat, s.statut]
      .filter(Boolean).join(' ').toLowerCase().includes(q);
  });

  /* ── Rendu ── */
  return (
    <div style={S.root}>
      {toast && (
        <div style={{
          position: 'fixed', bottom: 22, right: 22, zIndex: 9999,
          background: toast.type === 'error' ? '#b91c1c' : '#15803d',
          color: '#fff', padding: '12px 20px', borderRadius: 9,
          fontSize: 13.5, fontWeight: 600, maxWidth: 460,
        }}>{toast.msg}</div>
      )}

      <div style={S.header}>
        <div>
          <h2 style={S.title}>Solde de tout compte</h2>
          <p style={S.sub}>Calcul des droits de fin de contrat — CDI et CDD</p>
        </div>
        {isAdmin && tab === 'liste' && (
          <button style={btnP} onClick={nouveau}>+ Nouveau calcul</button>
        )}
      </div>

      <div style={S.tabBar}>
        <button style={S.tab(tab === 'liste')} onClick={() => setTab('liste')}>
          Soldes enregistrés
        </button>
        {(tab === 'form' || editId) && (
          <button style={S.tab(tab === 'form')} onClick={() => setTab('form')}>
            {editId ? 'Modifier le calcul' : 'Nouveau calcul'}
          </button>
        )}
      </div>

      {/* ══ LISTE ══ */}
      {tab === 'liste' && (
        <div style={S.card}>
          <div style={S.head}>
            <span style={S.cardTitle}>
              {listeFiltree.length} solde{listeFiltree.length > 1 ? 's' : ''} de tout compte
            </span>
            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
              <input
                value={rech} onChange={e => setRech(e.target.value)}
                placeholder="🔍 Rechercher (nom, matricule, statut…)"
                style={{ ...S.input, minWidth: 260 }}
              />
              <button
                style={{ ...btnV, opacity: exporting ? 0.7 : 1 }}
                disabled={exporting || !listeFiltree.length}
                onClick={() => exportToExcel({
                  rows: listeFiltree,
                  filename: `GFM3_Soldes_Tout_Compte_${new Date().toISOString().slice(0, 10)}.xlsx`,
                  sheetName: 'SDTC',
                  title: 'SOLDES DE TOUT COMPTE — DROITS DE FIN DE CONTRAT',
                  columns: [
                    { key: 'matricule',    label: 'Matricule',        width: 12 },
                    { key: 'nom',          label: 'Nom',              width: 18 },
                    { key: 'prenoms',      label: 'Prénoms',          width: 24 },
                    { key: 'type_contrat', label: 'Contrat',          width: 10 },
                    { key: 'date_depart',  label: 'Date de départ',   width: 15, format: fmtDate },
                    { key: 'salaire_brut_moyen',   label: 'Salaire brut moyen',  width: 18 },
                    { key: 'taux_indemnite',       label: 'Taux',                width: 10 },
                    { key: 'total_licenciement',   label: 'Indemnité licenc.',   width: 18 },
                    { key: 'indemnite_conges',     label: 'Indemnité congés',    width: 18 },
                    { key: 'total_preavis',        label: 'Préavis',             width: 16 },
                    { key: 'sdtc_total',           label: 'SDTC total',          width: 18 },
                    { key: 'quote_part_imposable', label: 'Quote-part imposable', width: 20 },
                    { key: 'statut',       label: 'Statut',           width: 12 },
                  ],
                })}
              >
                {exporting ? '⏳' : '📊'} Exporter Excel
              </button>
            </div>
          </div>

          <div style={{ overflowX: 'auto' }}>
            {loading ? (
              <div style={{ padding: 40, textAlign: 'center', color: '#94a3b8' }}>Chargement…</div>
            ) : listeFiltree.length === 0 ? (
              <div style={{ padding: 40, textAlign: 'center', color: '#94a3b8' }}>
                Aucun solde de tout compte enregistré
              </div>
            ) : (
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr>
                    <th style={S.th}>Employé(e)</th>
                    <th style={S.th}>Contrat</th>
                    <th style={S.th}>Départ</th>
                    <th style={{ ...S.th, textAlign: 'right' }}>Indemnité</th>
                    <th style={{ ...S.th, textAlign: 'right' }}>SDTC</th>
                    <th style={{ ...S.th, textAlign: 'right' }}>Imposable</th>
                    <th style={S.th}>Statut</th>
                    <th style={{ ...S.th, textAlign: 'center' }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {listeFiltree.map(s => (
                    <tr key={s.id}>
                      <td style={S.td}>
                        <div style={{ fontWeight: 700 }}>{s.nom} {s.prenoms}</div>
                        <div style={{ fontSize: 11, color: '#94a3b8' }}>{s.matricule}</div>
                      </td>
                      <td style={S.td}>
                        <span style={{
                          fontSize: 11, fontWeight: 700, padding: '3px 9px', borderRadius: 12,
                          background: s.type_contrat === 'CDI' ? '#e3f2fd' : '#fff3e0',
                          color: s.type_contrat === 'CDI' ? '#1565c0' : '#e65100',
                        }}>{s.type_contrat}</span>
                      </td>
                      <td style={S.td}>{s.date_depart ? fmtDate(s.date_depart) : '—'}</td>
                      <td style={{ ...S.td, textAlign: 'right', fontFamily: 'monospace' }}>
                        {fmt(s.total_licenciement)}
                      </td>
                      <td style={{ ...S.td, textAlign: 'right', fontFamily: 'monospace',
                                   fontWeight: 800, color: '#15803d' }}>
                        {fmt(s.sdtc_total)}
                      </td>
                      <td style={{ ...S.td, textAlign: 'right', fontFamily: 'monospace', color: '#c2410c' }}>
                        {fmt(s.quote_part_imposable)}
                      </td>
                      <td style={S.td}>
                        <span style={{
                          fontSize: 11, fontWeight: 700, padding: '3px 9px', borderRadius: 12,
                          background: s.statut === 'Payé' ? '#dcfce7'
                                    : s.statut === 'Validé' ? '#e0f2fe' : '#f1f5f9',
                          color: s.statut === 'Payé' ? '#15803d'
                               : s.statut === 'Validé' ? '#0369a1' : '#64748b',
                        }}>{s.statut}</span>
                      </td>
                      <td style={{ ...S.td, textAlign: 'center' }}>
                        <button style={{ ...btnG, padding: '5px 10px', marginRight: 6 }}
                          onClick={() => ouvrir(s.id)}>👁 Ouvrir</button>
                        {isAdmin && s.statut !== 'Payé' && (
                          <button style={{ ...S.btn, background: '#fef2f2', color: '#b91c1c', padding: '5px 10px' }}
                            onClick={() => supprimer(s.id)}>🗑</button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      )}

      {/* ══ FORMULAIRE + APERÇU ══ */}
      {tab === 'form' && (
        <>
          <div style={S.card}>
            <div style={S.head}>
              <span style={S.cardTitle}>Paramètres du calcul</span>
              <div style={{ display: 'flex', gap: 8 }}>
                <button style={btnG} onClick={() => { setTab('liste'); setEditId(null); }}>← Retour</button>
                <button style={btnP} onClick={genererPDF} disabled={pdfGen}>
                  {pdfGen ? '⏳ Génération…' : '📄 Générer PDF'}
                </button>
                {isAdmin && (
                  <>
                    <button style={btnG} onClick={() => enregistrer('Brouillon')} disabled={saving}>
                      💾 Brouillon
                    </button>
                    <button style={btnV} onClick={() => enregistrer('Validé')} disabled={saving}>
                      ✓ Valider
                    </button>
                  </>
                )}
              </div>
            </div>

            <div style={S.body}>
              {/* §3.60 — Rappel de la méthode propre au type de contrat */}
              {(() => {
                const cdi = form.type_contrat === 'CDI';
                return (
                  <div style={{
                    border: `1px solid ${cdi ? '#bfdbfe' : '#fed7aa'}`,
                    background: cdi ? '#eff6ff' : '#fff7ed',
                    borderRadius: 8, padding: '12px 16px', marginBottom: 18,
                  }}>
                    <div style={{
                      fontWeight: 800, fontSize: 12.5, marginBottom: 6,
                      color: cdi ? '#1565c0' : '#c2410c',
                    }}>
                      Méthode appliquée — {cdi ? 'CDI' : 'CDD'}
                    </div>
                    <div style={{ fontSize: 11.5, color: '#334155', lineHeight: 1.7 }}>
                      {cdi ? (
                        <>
                          <strong>Indemnité de licenciement</strong> = salaire brut moyen des
                          <strong> 12 derniers mois</strong> × taux d'ancienneté × quotité,
                          décomposée en 4 tranches (années · mois/12 · jours/360 · heures/2880).<br />
                          <strong>Taux</strong> : 30 % jusqu'à 5 ans · 35 % de 6 à 10 ans · 40 % au-delà.<br />
                          <strong>Base des congés</strong> : salaire brut moyen. Le rappel de prime
                          d'ancienneté est une ligne propre au CDI.
                        </>
                      ) : (
                        <>
                          <strong>Indemnité de fin de contrat</strong> = <strong>3 %</strong> du
                          cumul de tous les salaires bruts de la mission
                          (<strong>24 mois</strong> maximum).<br />
                          <strong>L'ancienneté n'intervient pas</strong> dans ce calcul : le taux est
                          forfaitaire.<br />
                          <strong>Base des congés et de la gratification</strong> : salaire brut
                          contractuel. Pas de rappel de prime d'ancienneté.
                        </>
                      )}
                    </div>
                  </div>
                );
              })()}

              {/* Identité & contrat */}
              <div style={S.grid}>
                <div>
                  <label style={S.label}>Employé(e) *</label>
                  <select style={S.input} value={form.employe_id}
                    onChange={e => choisirEmploye(e.target.value)}>
                    <option value="">— Sélectionner —</option>
                    {employes.map(e => (
                      <option key={e.id} value={e.id}>
                        {e.nom} {e.prenoms} ({e.matricule})
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label style={S.label}>Type de contrat</label>
                  <select style={S.input} value={form.type_contrat}
                    onChange={e => ajusterNbSalaires(e.target.value)}>
                    <option value="CDI">CDI</option>
                    <option value="CDD">CDD</option>
                  </select>
                </div>
                <div>
                  <label style={S.label}>Salaire de base (FCFA)</label>
                  <input type="number" style={S.input} value={form.salaire_base} onChange={f('salaire_base')} />
                </div>
                <div>
                  <label style={S.label}>Salaire de présence (FCFA)</label>
                  <input type="number" style={S.input} value={form.salaire_presence} onChange={f('salaire_presence')} />
                </div>
                <div>
                  <label style={S.label}>Salaire brut contractuel (FCFA)</label>
                  <input type="number" style={S.input} value={form.salaire_brut_contractuel}
                    onChange={f('salaire_brut_contractuel')} />
                </div>
                <div>
                  <label style={S.label}>Date d'embauche</label>
                  <input type="date" style={S.input} value={form.date_embauche || ''} onChange={f('date_embauche')} />
                </div>
                <div>
                  <label style={S.label}>Date de départ</label>
                  <input type="date" style={S.input} value={form.date_depart || ''} onChange={f('date_depart')} />
                </div>
              </div>

              {/* Ancienneté */}
              <div style={S.section}>
                Ancienneté et congés
                <span style={{ fontWeight: 400, fontSize: 11, marginLeft: 8,
                               color: form.type_contrat === 'CDI' ? '#1565c0' : '#c2410c' }}>
                  {form.type_contrat === 'CDI'
                    ? `détermine le taux — ${res.anciennete_annees.toFixed(2)} an(s) → ${pct(res.taux_indemnite)}`
                    : "sans effet sur l'indemnité CDD (taux forfaitaire de 3 %)"}
                </span>
              </div>
              <div style={S.grid}>
                <div><label style={S.label}>Années</label>
                  <input type="number" style={S.input} value={form.anciennete_ans} onChange={f('anciennete_ans')} /></div>
                <div><label style={S.label}>Mois</label>
                  <input type="number" style={S.input} value={form.anciennete_mois} onChange={f('anciennete_mois')} /></div>
                <div><label style={S.label}>Jours</label>
                  <input type="number" style={S.input} value={form.anciennete_jours} onChange={f('anciennete_jours')} /></div>
                <div><label style={S.label}>Heures</label>
                  <input type="number" style={S.input} value={form.anciennete_heures} onChange={f('anciennete_heures')} /></div>
                <div>
                  <label style={S.label}>Congés non pris (jours ouvrables)</label>
                  <input type="number" step="0.5" style={S.input} value={form.conges_non_pris} onChange={f('conges_non_pris')} />
                  <span style={{ fontSize: 10.5, color: '#64748b' }}>
                    = {res.conges_calendaires.toFixed(2)} jours calendaires (× {COEF_CALENDAIRE})
                  </span>
                </div>
              </div>

              {/* Salaires bruts */}
              <div style={S.section}>
                Salaires bruts mensuels — {nbMoisRef} derniers mois
                <span style={{ fontWeight: 400, fontSize: 11, color: '#64748b', marginLeft: 8 }}>
                  {form.type_contrat === 'CDI'
                    ? 'moyenne utilisée comme base de l\'indemnité'
                    : 'cumul utilisé comme base de l\'indemnité (3 %)'}
                </span>
              </div>
              <div style={{
                display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(160px,1fr))', gap: 10,
              }}>
                {(form.salaires || []).map((s, i) => (
                  <div key={i} style={{ border: '1px solid #e2e8f0', borderRadius: 7, padding: 8 }}>
                    <div style={{ fontSize: 10.5, fontWeight: 700, color: '#64748b', marginBottom: 4 }}>
                      Mois {i + 1}
                    </div>
                    <input type="month" style={{ ...S.input, marginBottom: 5, fontSize: 12 }}
                      value={(s.periode || '').slice(0, 7)}
                      onChange={e => majSalaire(i, 'periode', e.target.value ? `${e.target.value}-01` : '')} />
                    <input type="number" style={{ ...S.input, fontSize: 12 }} placeholder="Montant brut"
                      value={s.montant || ''} onChange={e => majSalaire(i, 'montant', e.target.value)} />
                  </div>
                ))}
              </div>

              {/* Préavis */}
              <div style={S.section}>
                Indemnité compensatrice de préavis
                <span style={{ fontWeight: 400, fontSize: 11, color: '#64748b', marginLeft: 8 }}>
                  base = salaire de base saisi plus haut + éléments ci-dessous
                </span>
              </div>
              <div style={S.grid}>
                <div>
                  <label style={S.label}>Salaire de base (repris)</label>
                  <input type="text" readOnly value={fmt(form.salaire_base)}
                    style={{ ...S.input, background: '#f1f5f9', color: '#475569',
                             fontWeight: 700, cursor: 'not-allowed' }} />
                  <span style={{ fontSize: 10.5, color: '#94a3b8' }}>
                    Modifiable uniquement en haut du formulaire
                  </span>
                </div>
                <div><label style={S.label}>Sursalaire</label>
                  <input type="number" style={S.input} value={form.preavis_sursalaire} onChange={f('preavis_sursalaire')} /></div>
                <div><label style={S.label}>Prime d'ancienneté</label>
                  <input type="number" style={S.input} value={form.preavis_prime_anciennete} onChange={f('preavis_prime_anciennete')} /></div>
                <div><label style={S.label}>Forfait heures suppl.</label>
                  <input type="number" style={S.input} value={form.preavis_forfait_hs} onChange={f('preavis_forfait_hs')} /></div>
                <div><label style={S.label}>Avantages en nature</label>
                  <input type="number" style={S.input} value={form.preavis_avantages} onChange={f('preavis_avantages')} /></div>
                <div><label style={S.label}>Nombre de mois de préavis</label>
                  <input type="number" step="0.5" style={S.input} value={form.preavis_nb_mois} onChange={f('preavis_nb_mois')} /></div>
              </div>

              {/* Autres indemnités */}
              <div style={S.section}>Autres indemnités</div>
              <div style={S.grid}>
                <div>
                  <label style={S.label}>Gratification — taux (vide = N/A)</label>
                  <input type="number" step="0.01" style={S.input} value={form.gratification_taux}
                    onChange={f('gratification_taux')} placeholder="ex : 0.75" />
                </div>
                <div><label style={S.label}>Gratification — prorata</label>
                  <input type="number" step="0.01" style={S.input} value={form.gratification_prorata}
                    onChange={f('gratification_prorata')} /></div>
                {form.type_contrat === 'CDI' && (
                  <div><label style={S.label}>Rappel prime d'ancienneté</label>
                    <input type="number" style={S.input} value={form.rappel_anciennete} onChange={f('rappel_anciennete')} /></div>
                )}
                <div><label style={S.label}>Transport — quote-part imposable</label>
                  <input type="number" style={S.input} value={form.transport_imp_montant} onChange={f('transport_imp_montant')} /></div>
                <div><label style={S.label}>Transport imposable — prorata</label>
                  <input type="number" step="0.01" style={S.input} value={form.transport_imp_prorata} onChange={f('transport_imp_prorata')} /></div>
                <div><label style={S.label}>Transport — non imposable</label>
                  <input type="number" style={S.input} value={form.transport_nonimp_montant} onChange={f('transport_nonimp_montant')} /></div>
                <div><label style={S.label}>Transport non imposable — prorata</label>
                  <input type="number" step="0.01" style={S.input} value={form.transport_nonimp_prorata} onChange={f('transport_nonimp_prorata')} /></div>
              </div>

              {/* Déductions */}
              <div style={S.section}>
                Lignes du solde de tout compte
                <span style={{ fontWeight: 400, fontSize: 11, color: '#64748b', marginLeft: 8 }}>
                  cochez pour exclure la ligne — le préavis coché est porté en déduction (négatif)
                </span>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(240px,1fr))', gap: 8 }}>
                {[
                  ['ded_licenciement',      form.type_contrat === 'CDD'
                                              ? 'Exclure l\'indemnité de fin de contrat'
                                              : 'Exclure l\'indemnité de licenciement'],
                  ['ded_salaire_presence',  'Exclure le salaire de présence'],
                  ['ded_conges',            'Exclure l\'indemnité de congé'],
                  ['ded_gratification',     'Exclure la gratification annuelle'],
                  ['ded_transport',         'Exclure l\'indemnité de transport'],
                  ['ded_preavis',           'Retenir le préavis en déduction (−)'],
                  ...(form.type_contrat === 'CDI'
                    ? [['ded_rappel_anciennete', 'Exclure le rappel de prime d\'ancienneté']] : []),
                ].map(([k, lab]) => (
                  <label key={k} style={{
                    display: 'flex', alignItems: 'center', gap: 8, fontSize: 12.5,
                    padding: '8px 10px', border: '1px solid #e2e8f0', borderRadius: 7,
                    background: form[k] ? '#fef2f2' : '#fff', cursor: 'pointer',
                  }}>
                    <input type="checkbox" checked={!!form[k]} onChange={f(k)} />
                    {lab}
                  </label>
                ))}
              </div>
            </div>
          </div>

          {/* ══ APERÇU DU DÉCOMPTE ══ */}
          <div style={S.card}>
            <div style={S.head}>
              <span style={S.cardTitle}>Décompte des droits</span>
            </div>
            <div ref={ficheRef} style={{ ...S.body, background: '#fff' }}>
              <div style={{
                display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start',
                borderBottom: '2px solid #0f2644', paddingBottom: 10, marginBottom: 14,
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                  <img src="/logo_gfm3.png" alt="GFM3" style={{ height: 54 }} />
                  <div>
                    <div style={{ fontWeight: 800, fontSize: 13.5, color: '#0f2644' }}>
                      GFM3 — Génération Femme du Troisième Millénaire
                    </div>
                    <div style={{ fontSize: 10, color: '#475569', lineHeight: 1.6, marginTop: 2 }}>
                      27 BP 283 Abidjan 27 | N°CNPS : 159633 | N°Contribuable : 0913190U
                    </div>
                  </div>
                </div>
                <div style={{ textAlign: 'right' }}>
                  <div style={{
                    background: '#0f2644', color: '#fff', fontWeight: 800, fontSize: 11.5,
                    padding: '6px 12px', borderRadius: 6, whiteSpace: 'nowrap',
                  }}>SOLDE DE TOUT COMPTE</div>
                  <div style={{ fontSize: 10.5, color: '#475569', marginTop: 6 }}>
                    Contrat : {form.type_contrat}<br />
                    Édité le {new Date().toLocaleDateString('fr-FR')}
                  </div>
                </div>
              </div>

              <table style={{ borderCollapse: 'collapse', width: '100%', marginBottom: 16, fontSize: 12 }}>
                <tbody>
                  {[
                    ['Employé(e)', form.nom_prenoms || '—'],
                    ['Matricule', form.matricule || '—'],
                    ["Date d'embauche", form.date_embauche ? fmtDate(form.date_embauche) : '—'],
                    ['Date de départ', form.date_depart ? fmtDate(form.date_depart) : '—'],
                    ['Ancienneté retenue',
                      `${form.anciennete_ans} an(s) ${form.anciennete_mois} mois ${form.anciennete_jours} jour(s)`],
                  ].map(([k, v]) => (
                    <tr key={k}>
                      <td style={{ border: '1px solid #cbd5e1', padding: '4px 9px', fontWeight: 700,
                                   background: '#f1f5f9', width: 190 }}>{k}</td>
                      <td style={{ border: '1px solid #cbd5e1', padding: '4px 9px' }}>{v}</td>
                    </tr>
                  ))}
                </tbody>
              </table>

              {/* Indemnité de licenciement / fin de contrat */}
              <div style={{ fontWeight: 800, fontSize: 12.5, color: '#0f2644', margin: '14px 0 6px' }}>
                {form.type_contrat === 'CDD'
                  ? 'Indemnité de fin de contrat'
                  : 'Indemnité de licenciement ou de départ'}
              </div>
              <table style={{ borderCollapse: 'collapse', width: '100%', fontSize: 11.5 }}>
                <thead>
                  <tr>
                    <th style={S.th}>Tranche</th>
                    <th style={{ ...S.th, textAlign: 'center' }}>Taux</th>
                    <th style={{ ...S.th, textAlign: 'right' }}>Quotité</th>
                    <th style={{ ...S.th, textAlign: 'right' }}>Montant</th>
                  </tr>
                </thead>
                <tbody>
                  {res.tranches.map((t, i) => (
                    <tr key={i}>
                      <td style={S.td}>{t.libelle}</td>
                      <td style={{ ...S.td, textAlign: 'center' }}>{pct(res.taux_indemnite)}</td>
                      <td style={{ ...S.td, textAlign: 'right', fontFamily: 'monospace' }}>
                        {form.type_contrat === 'CDD' ? fmt(t.quotite) : Number(t.quotite).toFixed(4)}
                      </td>
                      <td style={{ ...S.td, textAlign: 'right', fontFamily: 'monospace' }}>{fmt(t.montant)}</td>
                    </tr>
                  ))}
                  <tr style={{ background: '#f8fafc', fontWeight: 800 }}>
                    <td style={S.td} colSpan={3}>Total indemnité</td>
                    <td style={{ ...S.td, textAlign: 'right', fontFamily: 'monospace' }}>
                      {fmt(res.total_licenciement)}
                    </td>
                  </tr>
                </tbody>
              </table>
              <div style={{
                fontSize: 10.5, color: '#334155', marginTop: 6, padding: '7px 10px',
                background: form.type_contrat === 'CDI' ? '#eff6ff' : '#fff7ed',
                border: `1px solid ${form.type_contrat === 'CDI' ? '#bfdbfe' : '#fed7aa'}`,
                borderRadius: 5, lineHeight: 1.6,
              }}>
                <strong>Méthode {form.type_contrat}</strong> —{' '}
                {form.type_contrat === 'CDI'
                  ? <>salaire brut moyen sur {res.nb_salaires} mois
                      = <strong>{fmt(res.salaire_brut_moyen)} FCFA</strong>,
                      taux <strong>{pct(res.taux_indemnite)}</strong> pour une ancienneté
                      de {res.anciennete_annees.toFixed(2)} an(s), répartie en 4 tranches.</>
                  : <>cumul des salaires bruts sur {res.nb_salaires} mois
                      = <strong>{fmt(res.cumul_salaires)} FCFA</strong>,
                      taux forfaitaire <strong>{pct(res.taux_indemnite)}</strong>,
                      indépendant de l'ancienneté.</>}
              </div>

              {/* Décompte final */}
              <div style={{ fontWeight: 800, fontSize: 12.5, color: '#0f2644', margin: '18px 0 6px' }}>
                Solde de tout compte
              </div>
              <table style={{ borderCollapse: 'collapse', width: '100%', fontSize: 11.5 }}>
                <thead>
                  <tr>
                    <th style={S.th}>Élément</th>
                    <th style={{ ...S.th, textAlign: 'center' }}>Retenu</th>
                    <th style={{ ...S.th, textAlign: 'right' }}>Montant (FCFA)</th>
                  </tr>
                </thead>
                <tbody>
                  {res.lignes_sdtc.map(l => (
                    <tr key={l.code}>
                      <td style={S.td}>{l.libelle}</td>
                      <td style={{ ...S.td, textAlign: 'center',
                                   color: l.deduction ? '#b91c1c' : '#15803d', fontWeight: 700 }}>
                        {l.deduction ? (l.code === 'preavis' ? 'Déduction' : 'Non') : 'Oui'}
                      </td>
                      <td style={{ ...S.td, textAlign: 'right', fontFamily: 'monospace',
                                   color: l.montant < 0 ? '#b91c1c' : '#1e293b' }}>
                        {fmt(l.montant)}
                      </td>
                    </tr>
                  ))}
                  <tr style={{ background: '#dcfce7', fontWeight: 800 }}>
                    <td style={S.td} colSpan={2}>SOLDE DE TOUT COMPTE (SDTC)</td>
                    <td style={{ ...S.td, textAlign: 'right', fontFamily: 'monospace',
                                 fontSize: 14, color: '#15803d' }}>
                      {fmt(res.sdtc_total)}
                    </td>
                  </tr>
                </tbody>
              </table>

              {/* Traitement fiscal */}
              <div style={{ fontWeight: 800, fontSize: 12.5, color: '#0f2644', margin: '18px 0 6px' }}>
                Traitement fiscal
              </div>
              <table style={{ borderCollapse: 'collapse', width: '100%', fontSize: 11.5 }}>
                <tbody>
                  {[
                    ['Indemnité de licenciement non imposable', res.licenciement_non_imp,
                      `${res.total_licenciement >= SEUIL_EXONERATION ? '50 %' : '100 %'} — seuil ${fmt(SEUIL_EXONERATION)} FCFA`],
                    ['Indemnité de transport non imposable', res.transport_non_imposable, ''],
                  ].map(([k, v, note]) => (
                    <tr key={k}>
                      <td style={{ ...S.td, fontWeight: 600 }}>
                        {k}
                        {note && <div style={{ fontSize: 10, color: '#94a3b8' }}>{note}</div>}
                      </td>
                      <td style={{ ...S.td, textAlign: 'right', fontFamily: 'monospace', width: 160 }}>
                        {fmt(v)}
                      </td>
                    </tr>
                  ))}
                  <tr style={{ background: '#fff7ed', fontWeight: 800 }}>
                    <td style={S.td}>Quote-part SDTC imposable</td>
                    <td style={{ ...S.td, textAlign: 'right', fontFamily: 'monospace',
                                 fontSize: 13, color: '#c2410c' }}>
                      {fmt(res.quote_part_imposable)}
                    </td>
                  </tr>
                </tbody>
              </table>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20,
                            marginTop: 30, fontSize: 11 }}>
                <div>Date : ________________________</div>
                <div>Signature de l'employé(e) : ________________________</div>
                <div style={{ marginTop: 18 }}>Date : ________________________</div>
                <div style={{ marginTop: 18 }}>Visa de la Direction : ________________________</div>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
